import { useMemo, useState } from "react";
import { ChevronRightIcon, ChevronDownIcon } from "../../automationStudioIcons";
import styles from "../../styles/automationStudio.module.scss";
import { extractToolResponseText } from "../../utils/toolOutputExtract";
import { parseNetworkRequestsForDisplay } from "../../utils/networkFilter";

const NETWORK_REQUEST_TOOL = 'browser_network_requests';
const EVALUATE_TOOL = 'browser_evaluate';
const CONSOLE_TOOL = 'browser_console_messages';

const CONSOLE_LINE_REGEX = /^\[?(log|info|debug|warn|warning|error)\]?\s*[:>-]?\s*(.*)$/i;

function isFailedNetworkRow(row) {
    if (row.failed) return true;
    if (row.status == null) return false;
    return row.status >= 400;
}

function parseConsoleLines(text) {
    const body = extractResultSection(text);
    if (!body) return [];
    const out = [];
    for (const raw of body.split('\n')) {
        const line = raw.replace(/\s+$/, '');
        if (!line.trim()) continue;
        // Skip any stray MCP section heading (e.g. `### Page state`) that slipped past extraction.
        if (/^###\s/.test(line)) continue;
        const m = line.match(CONSOLE_LINE_REGEX);
        if (m) {
            const level = m[1].toLowerCase() === 'warning' ? 'warn' : m[1].toLowerCase();
            out.push({ level, message: m[2] });
        } else {
            out.push({ level: 'log', message: line });
        }
    }
    return out;
}

function tryParseJson(text) {
    if (!text) return null;
    const trimmed = text.trim();
    if (!(trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"'))) return null;
    try {
        return JSON.parse(trimmed);
    } catch {
        return null;
    }
}

// Playwright MCP wraps every tool response with `### Ran Playwright code` + the actual return
// value under `### Result` + a `### Page state` snapshot. Extract just the meaningful body so
// downstream parsers (evaluate JSON, console messages) don't see the wrapper headings.
function extractResultSection(text) {
    if (!text) return null;
    const resultMatch = text.match(/###\s*Result\s*\n([\s\S]*?)(?=\n###|$)/);
    if (resultMatch) return resultMatch[1].trim();
    const returnedMatch = text.match(/Returned:\s*([\s\S]+?)(?:\n###|$)/i);
    if (returnedMatch) return returnedMatch[1].trim();
    return text.trim();
}

function getNetworkStatusClass(row) {
    if (row.failed) return styles.toolOutputStatusError;
    if (row.status == null) return styles.toolOutputStatusPending;
    if (row.status >= 200 && row.status < 300) return styles.toolOutputStatusSuccess;
    if (row.status >= 300 && row.status < 400) return styles.toolOutputStatusRedirect;
    if (row.status >= 400 && row.status < 500) return styles.toolOutputStatusClientError;
    if (row.status >= 500) return styles.toolOutputStatusError;
    return styles.toolOutputStatusPending;
}

function getNetworkStatusLabel(row) {
    if (row.failed) return 'FAILED';
    if (row.status == null) return 'pending';
    return String(row.status);
}

function getConsoleLevelClass(level) {
    switch (level) {
        case 'error': return styles.toolOutputConsoleLevelError;
        case 'warn': return styles.toolOutputConsoleLevelWarn;
        case 'info': return styles.toolOutputConsoleLevelInfo;
        case 'debug': return styles.toolOutputConsoleLevelDebug;
        default: return styles.toolOutputConsoleLevelDefault;
    }
}

function buildTeaser(tool, text) {
    if (tool === NETWORK_REQUEST_TOOL) {
        if (!text) return 'No requests captured';
        const rows = parseNetworkRequestsForDisplay(text);
        if (!rows.length) return 'No relevant API requests';
        const errors = rows.filter(isFailedNetworkRow).length;
        const reqWord = rows.length === 1 ? 'request' : 'requests';
        return errors > 0 ? `${rows.length} ${reqWord} · ${errors} ${errors === 1 ? 'error' : 'errors'}` : `${rows.length} ${reqWord}`;
    }
    if (tool === CONSOLE_TOOL) {
        if (!text) return 'No messages';
        const rows = parseConsoleLines(text);
        if (!rows.length) return 'No messages';
        const errors = rows.filter(r => r.level === 'error').length;
        const msgWord = rows.length === 1 ? 'message' : 'messages';
        return errors > 0 ? `${rows.length} ${msgWord} · ${errors} ${errors === 1 ? 'error' : 'errors'}` : `${rows.length} ${msgWord}`;
    }
    if (tool === EVALUATE_TOOL) {
        if (!text) return 'No return value';
        const value = extractResultSection(text);
        if (!value) return 'No return value';
        const oneLine = value.replace(/\s+/g, ' ').trim();
        const truncated = oneLine.length > 60 ? `${oneLine.slice(0, 60)}…` : oneLine;
        return `Returned: ${truncated}`;
    }
    return null;
}

function NetworkBody({ rows, rawText }) {
    if (rows.length > 0) {
        return (
            <div className={styles.toolOutputNetworkList}>
                {rows.map((req, idx) => (
                    <div key={idx} className={styles.toolOutputNetworkRow}>
                        <span className={styles.toolOutputNetworkMethod}>{req.method}</span>
                        <span className={styles.toolOutputNetworkUrl} title={req.url}>
                            {req.pathOnly || req.url}
                        </span>
                        <span className={`${styles.toolOutputNetworkStatus} ${getNetworkStatusClass(req)}`}>
                            {getNetworkStatusLabel(req)}
                        </span>
                    </div>
                ))}
            </div>
        );
    }
    return <pre className={styles.toolOutputPre}>{rawText}</pre>;
}

function ConsoleBody({ rows, rawText }) {
    if (rows.length > 0) {
        return (
            <div className={styles.toolOutputConsoleList}>
                {rows.map((row, idx) => (
                    <div key={idx} className={styles.toolOutputConsoleRow}>
                        <span className={`${styles.toolOutputConsoleLevel} ${getConsoleLevelClass(row.level)}`}>
                            [{row.level}]
                        </span>
                        <span className={styles.toolOutputConsoleMessage}>{row.message}</span>
                    </div>
                ))}
            </div>
        );
    }
    return <pre className={styles.toolOutputPre}>{rawText}</pre>;
}

function EvaluateBody({ rawText }) {
    const value = extractResultSection(rawText);
    const parsed = tryParseJson(value);
    const display = parsed != null ? JSON.stringify(parsed, null, 2) : (value || rawText);
    return <pre className={styles.toolOutputPre}>{display}</pre>;
}

/**
 * Renders MCP tool output (collapsed by default) for supported tools.
 * Returns null when there is nothing to show.
 */
export default function ToolOutput({ step, stepDetails }) {
    const [open, setOpen] = useState(false);
    const tool = step?.tool;

    const supported = tool === NETWORK_REQUEST_TOOL || tool === EVALUATE_TOOL || tool === CONSOLE_TOOL;

    const text = useMemo(
        () => (supported ? extractToolResponseText(step, stepDetails) : null),
        [supported, step, stepDetails]
    );

    const networkRows = useMemo(
        () => (tool === NETWORK_REQUEST_TOOL && text ? parseNetworkRequestsForDisplay(text) : []),
        [tool, text]
    );
    const consoleRows = useMemo(
        () => (tool === CONSOLE_TOOL && text ? parseConsoleLines(text) : []),
        [tool, text]
    );

    if (!supported) return null;

    const teaser = buildTeaser(tool, text);
    const hasBody = !!text;

    return (
        <div className={styles.toolOutputBlock}>
            <button
                type="button"
                className={styles.toolOutputHeader}
                onClick={(e) => {
                    e.stopPropagation();
                    if (hasBody) setOpen((o) => !o);
                }}
                aria-expanded={open && hasBody}
                disabled={!hasBody}
            >
                {hasBody && (open
                    ? <ChevronDownIcon className={styles.toolOutputChevron} />
                    : <ChevronRightIcon className={styles.toolOutputChevron} />)}
                <span className={styles.toolOutputLabel}>Tool output</span>
                {teaser && <span className={styles.toolOutputTeaser}>{teaser}</span>}
            </button>
            {open && hasBody && (
                <div className={styles.toolOutputBody} onClick={(e) => e.stopPropagation()}>
                    {tool === NETWORK_REQUEST_TOOL && <NetworkBody rows={networkRows} rawText={text} />}
                    {tool === CONSOLE_TOOL && <ConsoleBody rows={consoleRows} rawText={text} />}
                    {tool === EVALUATE_TOOL && <EvaluateBody rawText={text} />}
                </div>
            )}
        </div>
    );
}
